import type { PendingWorkOrigin } from '@/services/sync/PendingSyncSession';
import { DEFERRED_MEDIA_CLEANUP_TABLE } from './DeferredMediaCleanup';

export const CHECKLIST_DELETION_TABLES = [
  'guincho_checklists', 'guincho_valores_checklist', 'pending_operations',
  'pending_images', DEFERRED_MEDIA_CLEANUP_TABLE,
] as const;

export interface ChecklistDeletionInput {
  checklistId: string;
  intentId: string;
  origin: PendingWorkOrigin;
  expectedPartnerRevision?: number;
  canCommit: () => boolean;
  onStage?: (stage: 'retention' | 'outbox' | 'commit') => void;
}

interface Store {
  all(table: string): Promise<Record<string, any>[]>;
  put(table: string, row: Record<string, any>): Promise<void>;
  remove(table: string, id: string): Promise<void>;
  queueDelete(table: string, row: Record<string, any>): Promise<string>;
}

/** Deve executar dentro de uma única transação que inclua todas as tabelas acima. */
export async function deleteChecklistInTransaction(input: ChecklistDeletionInput, store: Store): Promise<boolean> {
  if (!input.canCommit()) throw new Error('checklist_deletion_session_changed');
  const parent = (await store.all('guincho_checklists')).find(row => row.id === input.checklistId);
  if (!parent) {
    const prior = (await store.all(DEFERRED_MEDIA_CLEANUP_TABLE)).find(row => row.id === input.intentId);
    if (!prior) return false;
    if (prior.checklist_id !== input.checklistId || prior.user_id !== input.origin.userId || prior.empresa_id !== input.origin.empresaId) throw new Error('cleanup_idempotency_mismatch');
    return true;
  }
  if (!input.origin?.userId || parent.empresa_id !== input.origin.empresaId) throw new Error('checklist_deletion_origin_mismatch');
  const values = (await store.all('guincho_valores_checklist')).filter(row => row.checklist_id === parent.id);
  if (values.some(row => row.empresa_id !== input.origin.empresaId)) throw new Error('checklist_deletion_value_origin_mismatch');
  const valueIds = new Set(values.map(row => row.id));
  const operations = (await store.all('pending_operations')).filter(row =>
    (row.collection === 'guincho_checklists' && row.item_id === parent.id) ||
    (row.collection === 'guincho_valores_checklist' && (row.item_id === parent.id || valueIds.has(row.item_id))));
  const uploads = (await store.all('pending_images')).filter(row => row.item_type === 'CHECKLIST' && row.item_id === parent.id);
  if ([...operations, ...uploads].some(row => row.empresa_id && row.empresa_id !== input.origin.empresaId)) {
    throw new Error('checklist_deletion_queue_origin_mismatch');
  }
  const logicalDeletes: { collection: string; itemId: string; operationId: string }[] = [];
  const evidence = { id: input.intentId, user_id: input.origin.userId, empresa_id: input.origin.empresaId,
    checklist_id: parent.id, field_id: null, created_at: Date.now(),
    payload: JSON.stringify({ schema_version: 1, kind: 'checklist-delete', state: 'deferred', physical_deletion_allowed: false,
      parent, values, supersededOperations: operations, supersededUploads: uploads }) };
  if ((await store.all(DEFERRED_MEDIA_CLEANUP_TABLE)).some(row => row.id === input.intentId)) throw new Error('cleanup_idempotency_mismatch');
  await store.put(DEFERRED_MEDIA_CLEANUP_TABLE, evidence);
  input.onStage?.('retention');
  // O CREATE do pai permanece para a barreira autoritativa de cancelamento offline.
  for (const operation of operations) {
    if (operation.collection === 'guincho_checklists' && operation.operation === 'create') continue;
    await store.remove('pending_operations', operation.id);
  }
  for (const upload of uploads) await store.remove('pending_images', upload.id);
  for (const value of values) {
    logicalDeletes.push({ collection: 'guincho_valores_checklist', itemId: value.id,
      operationId: await store.queueDelete('guincho_valores_checklist', value) });
    await store.remove('guincho_valores_checklist', value.id);
  }
  logicalDeletes.push({ collection: 'guincho_checklists', itemId: parent.id,
    operationId: await store.queueDelete('guincho_checklists', parent) });
  await store.put(DEFERRED_MEDIA_CLEANUP_TABLE, { ...evidence,
    payload: JSON.stringify({ ...JSON.parse(evidence.payload), logicalDeletes }) });
  input.onStage?.('outbox');
  await store.remove('guincho_checklists', parent.id);
  if (!input.canCommit()) throw new Error('checklist_deletion_session_changed');
  input.onStage?.('commit');
  return true;
}
